import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

const Paginationx = ({ getPageMovies, npages, currentPage }) => {
  
  const changePage = (page) => {
    if (page >= 1 && page <= npages && page !== currentPage) {
      getPageMovies(page);
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(npages - 1, currentPage + delta); 
         i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < npages - 1) {
      rangeWithDots.push('...', npages);
    } else if (npages > 1) {
      rangeWithDots.push(npages);
    }

    return rangeWithDots;
  };

  // Don't render if there's only one page or no pages
  if (npages <= 1) {
    return null;
  }

  const visiblePages = getVisiblePages();

  return (
    <div className="flex justify-center items-center mt-12">
      <nav className="flex items-center space-x-1" aria-label="Pagination">
        
        {/* Previous Button */}
        <button
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage === 1}
          className={`
            relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
            ${currentPage === 1
              ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white shadow-sm hover:shadow-md'
            }
          `}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {visiblePages.map((page, index) => {
            if (page === '...') {
              return (
                <span
                  key={`dots-${index}`}
                  className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </span>
              );
            }

            const isCurrentPage = page === currentPage;

            return (
              <button
                key={page}
                onClick={() => changePage(page)}
                className={`
                  relative inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 min-w-[40px] justify-center
                  ${isCurrentPage
                    ? 'bg-blue-600 text-white shadow-lg hover:bg-blue-700 border border-blue-600'
                    : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white shadow-sm hover:shadow-md'
                  }
                `}
                aria-current={isCurrentPage ? 'page' : undefined}
                aria-label={`Go to page ${page}`}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          onClick={() => changePage(currentPage + 1)}
          disabled={currentPage === npages}
          className={`
            relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
            ${currentPage === npages
              ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white shadow-sm hover:shadow-md'
            }
          `}
          aria-label="Next page"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </nav>

      {/* Page Info */}
      <div className="ml-6 hidden lg:flex items-center text-sm text-gray-500 dark:text-gray-400">
        <span>
          Page <span className="font-semibold text-gray-900 dark:text-white">{currentPage}</span> of{' '}
          <span className="font-semibold text-gray-900 dark:text-white">{npages.toLocaleString()}</span>
        </span>
      </div>
    </div>
  );
};

export default Paginationx;